<template>
  <v-container fluid class="posts-container">
    <div v-if="loading">
      <ul>
        <li>
          <PostContentLoader />
        </li>
        <li>
        <div v-if="loadingMore" class="infinite-scroll-loading">
          <PostContentLoader />
        </div>
          <PostContentLoader />
        </li>
      </ul>
    </div>
    <div v-else>
      <div v-if="posts && posts.length > 0">
        <ul>
          <li
            v-for="post in posts"
            v-bind:key="post.id"
            :id="`post-${post.id}`"
          >
            <Post
              :draft="post.isDraft"
              :post="post"
              :addedDictionaryEntries="post.dictionaryEntries"
              :checklist="post.checklists"
              v-on:post-like-success="getPosts"
              v-on:post-like-failed="this.consoleLog('like failed')"
              v-on:post-read-failed="this.consoleLog('mark-as-read failed')"
              v-on:post-delete-success="getPosts"
              v-on:post-delete-failed="this.consoleLog('delete failed')"
              v-on:comment-delete-success="getPosts"
              v-on:comment-delete-failed="this.consoleLog('delete failed')"
              v-on:comment="postComment"
              v-on:comment-edit="editComment"
            ></Post>
          </li>
        </ul>
        <div
          :class="[
            this.$store.getters.user.role === 'n' || this.$store.getters.user.role === 'a' ? 'fab-no-tabs' : '',
            'fab-container'
          ]"
          v-if="this.$store.getters.user.role !== 'o'"
        >
        <div
          v-if="!loading && hasMorePosts"
          ref="infiniteScrollSentinel"
          class="infinite-scroll-sentinel"
        ></div>
          <Button
            class="fab"
            title
            color="#fff"
            bgcolor="#00A9B3"
            width="52px"
            height="52"
            margin="0"
            padding="0"
            icon="plus-white"
            @click.native="navigateBasedOnRole()"
          />
        </div>
      </div>
      <div v-else>
        <div class="display-message-container">
          <div class="display-message">
            <img
              :src="require('@/assets/images/duck-blue.svg')"
              alt="Duck icon"
            />
            <p  v-html="$t('general.post.not-found')"></p>
          </div>
        </div>
        <div
          :class="[
            this.$store.getters.user.role === 'n' || this.$store.getters.user.role === 'a' ? 'fab-no-tabs' : '',
            'fab-container'
          ]"
          v-if="this.$store.getters.user.role !== 'o'"
        >
          <Button
            class="fab"
            title
            color="#fff"
            bgcolor="#00A9B3"
            width="52px"
            height="52"
            margin="0"
            padding="0"
            icon="plus-white"
            @click.native="navigateBasedOnRole()"
          />
        </div>
      </div>
    </div>

     <NurseModal />
  </v-container>
</template>

<script>
import i18n from "@/services/i18n";
import {neoparentPatients, neoparentPosts} from "@/api";
import { getNotifications } from "@/helpers";

export default {
  i18n: i18n,
  name: "neoparent-posts",
  props: {
    postId: null
  },
  data() {
    return {
      loading: true,
      parents: null,
      posts: [],
      nextSkip: 0,
      hasMorePosts: true,
      loadingMore: false,
      pageSize: 15,
      scrollObserver: null
    };
  },
  mounted() {
    let nurse = this.$store.getters.nurse
    if (nurse){
      if (!nurse.language) {
        nurse.language = this.$store.getters.user.language
      }
      this.$store.commit('updateUser', nurse)
    }
     this.getPosts();
     getNotifications();
  },
  beforeDestroy() {
    this.teardownInfiniteScroll();
  },
  methods: {
    async getPosts() {
      this.teardownInfiniteScroll();
      this.loading = true;
      this.posts = [];
      this.nextSkip = 0;
      this.hasMorePosts = true;

      await this.loadNextPostBatch();

      this.$store.commit("clearPost");
      this.loading = false;

      this.$nextTick(() => {
        this.setupInfiniteScroll();
      });
    },
    async loadNextPostBatch() {
      if (!this.hasMorePosts || this.loadingMore) {
        return;
      }

      this.loadingMore = true;
      const minDelay = this.debugSlowLoadEnabled()
        ? new Promise(resolve => setTimeout(resolve, 3000))
        : Promise.resolve();

      try {
        const result = await neoparentPosts.getPostsForPatientPage(
          this.$store.getters.nurse.patient.id,
          this.nextSkip,
          this.pageSize
        );

        await minDelay;

        const fetchedPosts = result.data.posts || [];
        this.posts = this.posts.concat(fetchedPosts);
        this.nextSkip = result.data.nextSkip || this.nextSkip;
        this.hasMorePosts = !!result.data.hasMore;
        if (!this.hasMorePosts) {
          this.teardownInfiniteScroll();
        }
      } catch (err) {
        this.consoleLog(err);
        this.hasMorePosts = false;
        this.teardownInfiniteScroll();
      } finally {
        this.loadingMore = false;
      }
    },
    debugSlowLoadEnabled() {
      return this.$store.getters.debugSlowLoad === true;
    },
    setupInfiniteScroll() {
      if (!this.hasMorePosts || this.scrollObserver) {
        return;
      }

      const sentinel = this.$refs.infiniteScrollSentinel;
      if (!sentinel) {
        return;
      }

      this.scrollObserver = new IntersectionObserver(async entries => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting) {
          return;
        }

        await this.loadNextPostBatch();
      }, {
        root: null,
        rootMargin: "350px 0px",
        threshold: 0.01
      });

      this.scrollObserver.observe(sentinel);
    },
    teardownInfiniteScroll() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
        this.scrollObserver = null;
      }
    },
    postComment({ postId, comment, callback }) {
      neoparentPosts
        .addCommentToPost(postId, {
          content: comment
        })
        .then(result => {
          this.consoleLog(result);
          this.getPosts();
          callback();
        })
        .catch(err => {
          this.consoleLog(err);
        });
    },
    editComment(data) {
      neoparentPosts
        .editComment(data.postId, data.commentId, {
          id: data.commentId,
          content: data.content
        })
        .then(result => {
          this.consoleLog(result);
          this.getPosts();
        })
        .catch(err => {
          this.consoleLog(err);
        });
    },
    navigateBasedOnRole: function() {
      if (this.$store.getters.user.role === 'p') {
        window.location.href = "/create/post";
      } else {
        this.$modal.show('nurse-modal');
      }
    }
  }
};
</script>

<style scoped>
.posts-container {
  padding-left: 0;
  padding-right: 0;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

li:not(:last-child) {
  border-bottom: 3px solid #afdfd3;
}

li {
  min-width: 100%;
}

.tabcontainer {
  padding-bottom: 40px;
}

.infinite-scroll-sentinel {
  height: 1px;
}

.infinite-scroll-loading {
  padding: 12px 0;
}

/*
  Mirror the dashboard help button position (left) on the right side.
  Help button effective bottom offsets are:
  - desktop: 16px base + 35px margin = 51px
  - <=1024px: 16px base + 18px margin = 34px
  - coarse pointer: 16px base + 12px margin = 28px
*/
.fab {
  position: fixed !important;
  right: 16px !important;
  bottom: 51px !important;
}

@media (max-width: 768px) {
  .posts-container {
    padding-left: 0;
    padding-right: 0;
  }

  li {
    padding: 0 0 8px;
  }

  .fab {
    box-shadow: 0 8px 22px rgba(0, 169, 179, 0.3);
  }

  .display-message-container {
    padding: 0 8px;
  }

  .display-message {
    text-align: center;
  }
}

@media (max-width: 1024px) {
  .fab {
    bottom: 34px !important;
  }
}

@media (hover: none) and (pointer: coarse) {
  .fab {
    bottom: 28px !important;
  }
}
</style>